import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useColorModeValue,
  Text,
  Flex,
  Stack,
  SimpleGrid,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  useDisclosure,
  HStack,
  VStack,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import Loading from "components/Loading/Loading";
import { TrafficViolationImagesForm } from "views/Pages/TrafficViolation/TrafficViolationImagesForm";
import { CustomProgressBar } from "components/CustomProgressBar/CustomProgressBar";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { SidebarContext } from "contexts/SidebarContext";
import { checkErrors, getMessageRecentViolation, getRecentViolationId, hasError, maskCPF, validateUf, validateVehiclePlate } from "utils/forms-utils";
import {
  isAitValidationRequired,
  getDateTimeFromServer,
  validateAitDate,
  validateAitTime,
  handleAitInputChange,
  toServerDate,
  formatAitTypeDisplayLabel,
} from "utils/traffic-violation-utils";
import { ROUTES } from "constants/routes-path";
import { trafficViolationService, ViolationStatus } from "services/trafficViolationService";
import { violationAllotmentsValidationService, AllotmentStatus } from "services/violationAllotmentsValidationService";
import { vehicleService } from "services/vehicleService";
import { trafficViolationTypeService } from "services/trafficViolationTypeService";
import { base64ToFile, getExtensionFromBase64, ufs, validateCPF } from "../../../utils";
import Magnifier from "../../../components/Magnifier/Magnifier";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { FaTrash } from "react-icons/fa";
import { FaRotate } from "react-icons/fa6";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR, TIME_FORMAT } from "constants/date-time";

const ViolationAllotmentValidate = () => {
  const { t } = useTranslation();
  const { allotmentId, violationId } = useParams();
  const { isOpen, onToggle } = useDisclosure();
  const { updateBreadcrumbKeyParams } = useContext(SidebarContext);
  const [allotment, setAllotment] = useState(undefined);
  const [allotmentViolations, setAllotmentViolations] = useState(undefined);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentViolation, setCurrentViolation] = useState(undefined);
  const [aitTypes, setAitTypes] = useState(undefined);
  const [formData, setFormData] = useState(undefined);
  const [imagePath1, setImagePath1] = useState(undefined);
  const [imagePath2, setImagePath2] = useState(undefined);
  const [inputError, setInputError] = useState({});
  const imagesRef = useRef(undefined);
  const observationsTextareaRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const textColor = useColorModeValue("gray.700", "white");
  const [rotation1, setRotation1] = useState(0);
  const [rotation2, setRotation2] = useState(0);
  const [indexesToRemove, setIndexesToRemove] = useState(new Set());
  const [duplicateInfraction, setDuplicateInfraction] = useState(null);
  const [isSearchingVehicle, setIsSearchingVehicle] = useState(undefined);


  const cardSpacing = "20px";
  const formRowSpacing = "10px";
  const translationKey = "violation-allotments.validate";
  const formTranslationKey = `${translationKey}.form`;
  const lensMagnifierSize = 300;
  const ids = useMemo(() => ({
    vehiclePlate: "vehicle-plate",
    vehiclePlateCity: "vehicle-plate-city",
    vehicleUfPlate: "vehicle-uf-plate",
    vehicleOwnerName: "vehicle-owner-name",
    vehicleColor: "vehicle-color",
    vehicleModel: "vehicle-model",
    vehicleCategory: "vehicle-category",
    vehicleModelYear: "vehicle-model-year",
    vehicleType: "vehicle-type",
    aitNumber: "ait-number",
    aitAgent: "ait-agent",
    aitType: "ait-type",
    aitDate: "ait-date",
    aitTime: "ait-time",
    aitObservations: "ait-observations",
    aitFirstImage: "ait-first-image",
    aitSecondImage: "ait-second-image",
    aitInvalidateJustification: "ait-invalidate-justification",
    aitInvalidateObservations: "ait-invalidate-observations",
    locationStreet: "location-street",
    locationNeighborhood: "location-neighborhood",
    locationReference: "location-reference",
    driverUfCnh: "driver-uf-cnh",
    driverName: "driver-name",
    driverCnhRegistration: "driver-cnh-registration",
    driverCpf: "driver-cpf",
  }), []);

  const handleVehicleSearch = async (plateToSearch) => {
    setIsSearchingVehicle(true);

    try {
      const result = await vehicleService.findByPlate(plateToSearch);

      setFormData((prev) => {
        const vehicleFormData = {
          [ids.vehicleOwnerName]: { ...prev[ids.vehicleOwnerName], value: result.nomeProprietario?.trim() || "" },
          [ids.vehicleUfPlate]: { ...prev[ids.vehicleUfPlate], value: result.ufPlaca?.trim() || "" },
          [ids.vehicleCategory]: { ...prev[ids.vehicleCategory], value: result.descricaoCategoria?.trim() || "" },
          [ids.vehicleModel]: { ...prev[ids.vehicleModel], value: result.descricaoMarcaModelo?.trim() || "" },
          [ids.vehicleColor]: { ...prev[ids.vehicleColor], value: result.descricaoCor?.trim() || "" },
          [ids.vehicleType]: { ...prev[ids.vehicleType], value: result.descricaoTipo?.trim() || "" },
          [ids.vehicleModelYear]: { ...prev[ids.vehicleModelYear], value: result.anoModelo?.toString().trim() || "" },
          [ids.vehiclePlateCity]: {...prev[ids.vehiclePlateCity], value: result.cidadeImovelProprietario?.trim() || ""}
        };

        const newFormData = { ...prev, ...vehicleFormData };
        setInputError((prevErrors) => checkErrors({ formData: newFormData, inputError: prevErrors }));
        return newFormData;
      });

    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }

    try {
      const filterParams = {
        orderby: "data_infracao",
        orderDirection: "desc",
        pageSize: 1,
        filters: { placa: plateToSearch }
      };
      const data = await trafficViolationService.list(filterParams);
      if (data?._embedded) {
        const recentViolation = getRecentViolationId(data?._embedded?.content);
        if (recentViolation) {
          const violationData = await trafficViolationService.find(recentViolation.id);
          showToast({
            title: getMessageRecentViolation(recentViolation, violationData),
            status: "warning"
          });
        }
      }
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    } finally {
      setIsSearchingVehicle(false);
    }
  };

  const invalidateJustifications = useMemo(() => [
    ViolationStatus.CANCELED,
    ViolationStatus.DIVERGENT,
    ViolationStatus.AGENT_ERROR,
    ViolationStatus.OTHERS,
    ViolationStatus.WARNING,
    ViolationStatus.OTHER_STATES,
    ViolationStatus.NOT_REGISTERED,
    ViolationStatus.UNREADABLE,
    ViolationStatus.DUPLICATED,
    ViolationStatus.ERROR_REG_EQUIP,
    ViolationStatus.VEHICLE_EMERGENCY,
  ], []);

  const isFormInvalid = formData && inputError ? hasError({
    formData: { ...formData, [ids.aitInvalidateJustification]: {} },
    inputError: { ...inputError, [ids.aitInvalidateJustification]: undefined },
  }) : true;

  const initFormData = (data = undefined) => {
    const { aitDate, aitTime } = getDateTimeFromServer(data?.data_infracao);

    return {
      [ids.vehiclePlate]: {
        value: data?.placa_veiculo ?? "",
        validations: [
          { required: true },
          { callback: (args) => validateVehiclePlate(args, `${formTranslationKey}.fields.vehicle-plate.label`) },
        ],
      },
      [ids.vehiclePlateCity]: {
        value: data?.nome_municipio_imovel_proprietario_veiculo_ait ?? "",
        validations: [],
      },
      [ids.vehicleUfPlate]: {
        value: (data?.uf_placa_veiculo != null && String(data.uf_placa_veiculo).trim() !== "") ? data.uf_placa_veiculo : "  ",
        validations: [
          { required: true },
          { callback: (args) => !args.value?.trim() ? null : validateUf(args) },
        ],
      },
      [ids.vehicleOwnerName]: {
        value: (data?.nome_proprietario_veiculo_ait != null && String(data.nome_proprietario_veiculo_ait).trim() !== "") ? data.nome_proprietario_veiculo_ait : " ",
        validations: [{ required: true }],
      },
      [ids.vehicleColor]: {
        value: (data?.descricao_cor_veiculo != null && String(data.descricao_cor_veiculo).trim() !== "") ? data.descricao_cor_veiculo : " ",
        validations: [{ required: true }],
      },
      [ids.vehicleModel]: {
        value: (data?.descricao_marca_modelo_veiculo != null && String(data.descricao_marca_modelo_veiculo).trim() !== "") ? data.descricao_marca_modelo_veiculo : " ",
        validations: [{ required: true }],
      },
      [ids.vehicleCategory]: {
        value: (data?.descricao_categoria_veiculo != null && String(data.descricao_categoria_veiculo).trim() !== "") ? data.descricao_categoria_veiculo : " ",
        validations: [{ required: true }],
      },
      [ids.vehicleModelYear]: {
        value: data?.ano_modelo_veiculo ?? "",
        validations: [{ required: true }],
      },
      [ids.vehicleType]: {
        value: (data?.descricao_tipo_veiculo != null && String(data.descricao_tipo_veiculo).trim() !== "") ? data.descricao_tipo_veiculo : " ",
        validations: [{ required: true }],
      },
      [ids.aitNumber]: {
        value: data?.numero_ait ?? "",
        validations: [{ required: true }],
      },
      [ids.aitAgent]: {
        value: (data?.codigo_agente && data?.descricao_agente) ? `${data.codigo_agente} - ${data.descricao_agente}` : "",
        validations: [{ required: true }],
      },
      [ids.aitType]: {
        value: data?.id_tipo_infracao?.id ?? 0,
        validations: [{ required: true }],
      },
      [ids.aitDate]: {
        value: aitDate ?? "",
        validations: [{ callback: (args) => validateAitDate(args, ids) }],
      },
      [ids.aitTime]: {
        value: aitTime ?? "",
        validations: [{ callback: (args) => validateAitTime(args, ids) }],
      },
      [ids.aitObservations]: {
        value: (data?.observacoes != null && String(data.observacoes).trim() !== "") ? data.observacoes : " ",
        validations: [{ required: true }],
      },
      [ids.aitInvalidateJustification]: {
        value: "",
        validations: [{ required: true }],
      },
      [ids.aitInvalidateObservations]: {
        value: "",
        validations: [],
      },
      [ids.locationStreet]: {
        value: data?.logradouro_local_infracao ?? "",
        validations: [{ required: true }],
      },
      [ids.locationNeighborhood]: {
        value: data?.bairro_local_infracao ?? "",
        validations: [],
      },
      [ids.locationReference]: {
        value: data?.complemento_local_infracao ?? "",
        validations: [{ required: true }],
      },
      [ids.driverUfCnh]: {
        value: data?.uf_cnh_condutor ?? "",
        validations: [],
      },
      [ids.driverName]: {
        value: data?.nome_condutor ?? "",
        validations: [],
      },
      [ids.driverCnhRegistration]: {
        value: data?.registro_cnh_condutor ?? "",
        validations: [],
      },
      [ids.driverCpf]: {
        value: data?.cpf_condutor ?? "",
        validations: [
          {
            callback: ({ value }) =>
              value ? validateCPF(maskCPF(value)) ? null : t("process.messages.errors.cpf-format") : null,
          }
        ],
      },
    };
  }

  const handleInputChange = (event) => {
    const id = event.target.id;
    let value = event.target.value;

    if (id === ids.vehicleUfPlate) {
      const onlyLetters = value.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase();
      const updated = {
        ...formData,
        [ids.vehicleUfPlate]: { ...formData[ids.vehicleUfPlate], value: onlyLetters },
      };
      setFormData(updated);
      setInputError((prev) => checkErrors({ formData: updated, inputError: prev, name: ids.vehicleUfPlate }));
      return;
    }

    if (id === ids.aitObservations) {
      const textarea = event.target;
      const newValue = value.toUpperCase();
      pendingSelectionRef.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
      setFormData((prev) => {
        const next = { ...prev, [ids.aitObservations]: { ...prev[ids.aitObservations], value: newValue } };
        setInputError((prevErr) => checkErrors({ formData: next, inputError: prevErr, name: ids.aitObservations }));
        return next;
      });
      return;
    }

    handleAitInputChange(event, ids, formData, setFormData, inputError, setInputError);

    if (id === ids.vehiclePlate) {
      const cleanPlate = value.replace(/[^a-zA-Z0-9]/g, '');

      if (cleanPlate.length === 7) {
        handleVehicleSearch(cleanPlate);
      }
    }
  };

  const handleAitDateChange = (value) => {
    const m = moment(value);
    const date = m.isValid() ? m.format(DATE_FORMAT_ISO) : "";

    const updated = {
      ...formData,
      [ids.aitDate]: {
        ...formData[ids.aitDate],
        value: date,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: ids.aitDate })
    );
  };

  const handleAitObservationsFocus = () => {
    if (formData?.[ids.aitObservations]?.value === " ") {
      setFormData((prev) => ({
        ...prev,
        [ids.aitObservations]: { ...prev[ids.aitObservations], value: "" },
      }));
    }
  };

  const handleVehicleUfPlateFocus = () => {
    if (formData?.[ids.vehicleUfPlate]?.value === "  ") {
      setFormData((prev) => ({
        ...prev,
        [ids.vehicleUfPlate]: { ...prev[ids.vehicleUfPlate], value: "" },
      }));
    }
  };

  const handleVehicleCategoryFocus = () => {
    if (formData?.[ids.vehicleCategory]?.value === " ") {
      setFormData((prev) => ({
        ...prev,
        [ids.vehicleCategory]: { ...prev[ids.vehicleCategory], value: "" },
      }));
    }
  };

  const handleVehicleTypeFocus = () => {
    if (formData?.[ids.vehicleType]?.value === " ") {
      setFormData((prev) => ({
        ...prev,
        [ids.vehicleType]: { ...prev[ids.vehicleType], value: "" },
      }));
    }
  };

  const handleVehicleOwnerNameFocus = () => {
    if (formData?.[ids.vehicleOwnerName]?.value === " ") {
      setFormData((prev) => ({
        ...prev,
        [ids.vehicleOwnerName]: { ...prev[ids.vehicleOwnerName], value: "" },
      }));
    }
  };

  const handleVehicleColorFocus = () => {
    if (formData?.[ids.vehicleColor]?.value === " ") {
      setFormData((prev) => ({
        ...prev,
        [ids.vehicleColor]: { ...prev[ids.vehicleColor], value: "" },
      }));
    }
  };

  const handleVehicleModelFocus = () => {
    if (formData?.[ids.vehicleModel]?.value === " ") {
      setFormData((prev) => ({
        ...prev,
        [ids.vehicleModel]: { ...prev[ids.vehicleModel], value: "" },
      }));
    }
  };

  const handleAitTimeChange = (value) => {
    const m = moment(value);
    const time = m.isValid() ? m.format(TIME_FORMAT) : "";

    const updated = {
      ...formData,
      [ids.aitTime]: {
        ...formData[ids.aitTime],
        value: time,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: ids.aitTime })
    );
  };

  const redirectToList = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.violationAllotmentsValidation);
  }

  const redirectToViolationList = () => {
    navigate(ROUTES.transportManagementPath + ROUTES.violationAllotmentsViolations.replace(":allotmentId", allotmentId));
  }

  const pendingFilter = (el) => isAitValidationRequired(el.status);

  const fetchAitTypes = async () => {
    const data = await trafficViolationTypeService.lookup();
    if (data.content <= 0) throw "traffic-violation-type.messages.errors.list";
    return data.map((it) => ({ ...it, displayLabel: formatAitTypeDisplayLabel(it) }));
  }

  const fetchCurrentValidation = async (allotmentViolations = []) => {
    if (violationId) return trafficViolationService.find(violationId);
    const pending = allotmentViolations.filter(pendingFilter);
    return trafficViolationService.find(pending[currentIndex].id);
  }

  const fetchCheckDuplicate = async (violationId = -1) => {
    const endpointCheckDuplicateData = await trafficViolationService.checkDuplicate(violationId);
    return endpointCheckDuplicateData;
  }

  const fetchAllotmentViolations = async () => {
    const data = await violationAllotmentsValidationService.listViolations(allotmentId);
    if (data.length <= 0 || (!violationId && !data.some(pendingFilter))) {
      throw "violation-allotments.messages.errors.list-violations";
    }
    return data;
  }

  const fetchAllotment = async () => {
    const data = await violationAllotmentsValidationService.find(allotmentId);
    if (data.status !== AllotmentStatus.OPEN.value) throw "violation-allotments.messages.errors.find";
    return data;
  }

  const fetchData = async () => {
    try {
      updateBreadcrumbKeyParams({});
      const allotment = await fetchAllotment();
      const allotmentViolations = await fetchAllotmentViolations();
      updateBreadcrumbKeyParams({
        allotmentName: allotment.nome,
        count: allotmentViolations.filter(pendingFilter).length - currentIndex,
        interpolation: { escapeValue: false }
      });

      const currentViolation = await fetchCurrentValidation(allotmentViolations);
      const aitTypes = await fetchAitTypes();
      const hasDuplicates = await fetchCheckDuplicate(currentViolation.id);
      setImagePath1(currentViolation.images[0]);
      setImagePath2(currentViolation.images[1])

      setAllotment(allotment);
      setAllotmentViolations(allotmentViolations);
      setCurrentViolation(currentViolation);
      setAitTypes(aitTypes);
      setFormData(initFormData(currentViolation));
      setDuplicateInfraction(hasDuplicates);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
      redirectToList();
    }
  }

  useEffect(() => {
    fetchData();
    return () => {
      updateBreadcrumbKeyParams({});
    };
  }, []);

  const observationsValue = formData?.[ids.aitObservations]?.value ?? "";
  useEffect(() => {
    if (observationsTextareaRef.current && pendingSelectionRef.current) {
      const { start, end } = pendingSelectionRef.current;
      observationsTextareaRef.current.setSelectionRange(start, end);
      pendingSelectionRef.current = null;
    }
  }, [observationsValue]);

  const toDTO = (status) => {
    const ufValue = formData[ids.vehicleUfPlate].value.trim();
    const ufPlacaVeiculo = (!ufValue || !ufs.some((e) => e.value === ufValue.toUpperCase())) ? "  " : ufValue;
    return {
    "traffic_violation_id": String(currentViolation.id),
    "placa_veiculo": formData[ids.vehiclePlate].value.trim(),
    "cidade_imovel_proprietario_veiculo": formData[ids.vehiclePlateCity].value.trim(),
    "nome_proprietario_veiculo_ait": (formData[ids.vehicleOwnerName].value.trim() || " "),
    "uf_placa_veiculo": ufPlacaVeiculo,
    "descricao_categoria_veiculo": (formData[ids.vehicleCategory].value.trim() || " "),
    "descricao_marca_modelo_veiculo": (formData[ids.vehicleModel].value.trim() || " "),
    "descricao_cor_veiculo": (formData[ids.vehicleColor].value.trim() || " "),
    "descricao_tipo_veiculo": (formData[ids.vehicleType].value.trim() || " "),
    "ano_modelo_veiculo": Number(formData[ids.vehicleModelYear].value),
    "uf_condutor": formData[ids.driverUfCnh].value.trim(),
    "nome_condutor": formData[ids.driverName].value.trim(),
    "numero_registro_indicacao_condutor": formData[ids.driverCnhRegistration].value.trim(),
    "documento_condutor": formData[ids.driverCpf].value.trim(),
    "numero_ait": formData[ids.aitNumber].value.trim(),
    "codigo_agente": currentViolation.codigo_agente,
    "id_tipo_infracao": formData[ids.aitType].value,
    "data_infracao": toServerDate(formData[ids.aitDate].value),
    "horario_infracao": formData[ids.aitTime].value,
    "observacoes": (formData[ids.aitObservations].value.trim() || " "),
    "logradouro_local_infracao": formData[ids.locationStreet].value.trim(),
    "bairro_local_infracao": formData[ids.locationNeighborhood].value.trim(),
    "complemento_local_infracao": formData[ids.locationReference].value.trim(),
    "status": status,
    "observacao_invalidacao": formData[ids.aitInvalidateObservations].value.trim() || null,
  };
  };

  const updateCurrentViolation = async (data) => {
    const nextAllotmentViolations = [...allotmentViolations];

    if (violationId) {
      const index = nextAllotmentViolations.findIndex(el => el.id === Number(violationId));
      nextAllotmentViolations[index].status = data["status"];
      setAllotmentViolations(nextAllotmentViolations);
      navigate(ROUTES.transportManagementPath + ROUTES.violationAllotmentsValidate.replace(":allotmentId", allotmentId));
    }

    const pending = nextAllotmentViolations.filter(pendingFilter);
    const nextIndex = violationId ? currentIndex : currentIndex + 1;

    if (nextIndex >= pending.length - 1) {
      showToast({ title: t("violation-allotments.messages.success.allotment-validated"), status: "success" });
      return redirectToList();
    }

    setFormData(undefined);
    setInputError({});

    const nextViolation = await trafficViolationService.find(pending[nextIndex].id);
    setCurrentIndex(nextIndex);
    setCurrentViolation(nextViolation);
    setFormData(initFormData(nextViolation));
    setImagePath1(nextViolation.images[0]);
    setImagePath2(nextViolation.images[1]);

  }

  const toFormData = (status) => {
    const formValues = toDTO(status);

    const data = new FormData();

    data.append("trafficViolation", new Blob([JSON.stringify(formValues)], { type: "application/json" }));

    const filesObject = imagesRef?.current?.getFiles();
    if (Object.keys(filesObject).length !== 0) {
      const filesArray = Object.values(filesObject);

      filesArray.forEach((img, idx) => {
        const file = img.file;

        if (indexesToRemove.has(idx + 1)) return;

        if (file instanceof File) {
          data.append("files", file);
        }
      });
    } else {
      if (imagePath1 && imagePath1.startsWith('data:')) {
        data.append("files", base64ToFile(imagePath1, data.get("numero_ait")));
      }

      if (imagePath2 && imagePath2.startsWith('data:')) {
        data.append("files", base64ToFile(imagePath2, "1" + data.get("numero_ait")));
      }
    }
    return data;
  };


  const handleInvalidate = async () => {
    try {
      onToggle();
      const data = toFormData(formData[ids.aitInvalidateJustification].value);
      await trafficViolationService.validate(currentViolation.id, data);
      showToast({ title: t("traffic-violation.messages.success.invalidate"), status: "success" });
      await updateCurrentViolation(data);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  }

  const handleValidate = async () => {
    if (!formData) return;
    try {
      const data = toFormData(ViolationStatus.VALIDATED.value);
      await trafficViolationService.validate(currentViolation.id, data);
      showToast({ title: t("traffic-violation.messages.success.validate"), status: "success" });
      await updateCurrentViolation(data);
    } catch (err) {
      showToast({ title: t(err), status: "error" });
    }
  }

  const handleRemoveImage = async (indexToRemove) => {
    if(indexToRemove===1){
      setImagePath1(null);
    }
    if(indexToRemove===2){
      setImagePath2(null);
    }

    setIndexesToRemove(prev => new Set(prev).add(indexesToRemove.size === 0 ? indexToRemove : 1));
  }

  const handleRotatePrimary = () => {
    setRotation1((prev) =>  prev + 90);
  };

  const handleRotateSecondary = () => {
    setRotation2((prev) =>  prev + 90);
  };

  return (
    <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
      {duplicateInfraction &&
        (<Card bg="#FAF089">
          <CardBody>
            <Text color="yellow.700">{t("violation-allotments.validate.duplicate_warning", { "ait_number": duplicateInfraction.numero_ait, "infraction_description": duplicateInfraction.codigo_tipo_infracao_desdobramento, "datetime": duplicateInfraction.data_hora })}</Text>
          </CardBody>
        </Card>
        )}
      {allotment && allotmentViolations && (
        <Flex w="350px" justify="center">
          <CustomProgressBar
            gap="5px"
            color="white"
            textAlign="center"
            total={allotmentViolations.length}
            current={allotmentViolations.length - allotmentViolations.filter(pendingFilter).length + currentIndex}
          />
        </Flex>
      )}
      <SimpleGrid columns={2} w="350px" justify="center" gap="0.5em">
        <Button
          variant="list-primary"
          onClick={onToggle}
        >
          {t(`${translationKey}.actions.invalidate`)}
        </Button>

        <Button
          variant="list-primary"
          onClick={handleValidate}
        >
          {t(`${translationKey}.actions.validate`)}
        </Button>
      </SimpleGrid>

      <SimpleGrid spacing="5" columns={{ sm: 1, lg: 2 }} w="100%" maxW="1400px">

        {(imagePath1 || imagePath2) && (
          <Stack align={{ lg: "flex-start" }}>
            <Card w="100%">
              <CardBody>
                <Stack gap="20px" wrap="wrap">
                  {imagePath1 && (
                    <VStack spacing={4} flex="1" mx="auto">
                      <Magnifier
                        src={imagePath1}
                        zoom={2.5}
                        lensSize={lensMagnifierSize}
                        rotation={rotation1}
                        height="50%"
                      />
                      <HStack spacing={6} justify="center">
                        <Button
                          leftIcon={<FaRotate w={2} h={2} />}
                          size="sm"
                          onClick={handleRotatePrimary}
                          colorScheme="blue"
                          aria-label={t("system.actions.rotate")}
                        >
                          <Text>{t(`${translationKey}.actions.rotate-image`)}</Text>
                        </Button>
                        <Button
                          leftIcon={<FaTrash w={2} h={2} />}
                          size="sm"
                          onClick={() => handleRemoveImage(1)}
                          colorScheme="red"
                          aria-label={t(`${translationKey}.actions.remove-image`)}
                        >
                          <Text>{t(`${translationKey}.actions.remove-image`)}</Text>
                        </Button>

                      </HStack>
                    </VStack>
                  )}

                  {imagePath2 && (
                    <VStack spacing={4} flex="1" mx="auto">
                      <Magnifier
                        src={imagePath2}
                        zoom={2.5}
                        lensSize={lensMagnifierSize}
                        rotation={rotation2}
                        height="50%"
                      />
                      <HStack spacing={6} justify="center">
                        <Button
                          leftIcon={<FaRotate w={2} h={2} />}
                          size="sm"
                          onClick={handleRotateSecondary}
                          colorScheme="blue"
                          aria-label={t("system.actions.rotate")}
                        >
                          <Text>{t(`${translationKey}.actions.rotate-image`)}</Text>
                        </Button>
                        <Button
                          leftIcon={<FaTrash w={2} h={2} />}
                          size="sm"
                          onClick={() => handleRemoveImage(2)}
                          colorScheme="red"
                          aria-label={t(`${translationKey}.actions.remove-image`)}
                        >
                          <Text>{t(`${translationKey}.actions.remove-image`)}</Text>
                        </Button>
                      </HStack>
                    </VStack>
                  )}
                </Stack>
              </CardBody>
            </Card>
          </Stack>
        )}

        <Stack direction="column" spacing={cardSpacing}>
          {/*Vehicle data card*/}
          <Card>
            <CardHeader mb="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold">
                {t(`${formTranslationKey}.title.vehicle-data`)}
              </Text>
            </CardHeader>

            <CardBody>
              {formData && !isSearchingVehicle ? (
                <Stack direction="column" spacing={formRowSpacing}>
                  {/*vehicle-plate*/}
                  <FormControl isRequired isInvalid={inputError[ids.vehiclePlate]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-plate.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.vehiclePlate}
                      value={formData[ids.vehiclePlate].value.toUpperCase()}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehiclePlate])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-owner-name*/}
                  <FormControl isInvalid={inputError[ids.vehicleOwnerName]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-owner-name.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehicleOwnerName}
                      value={formData[ids.vehicleOwnerName].value}
                      onChange={handleInputChange}
                      onFocus={handleVehicleOwnerNameFocus}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleOwnerName])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-plate-city*/}
                  <FormControl isInvalid={inputError[ids.vehiclePlateCity]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-plate-city.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehiclePlateCity}
                      value={formData[ids.vehiclePlateCity].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehiclePlateCity])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-uf-plate*/}
                  <FormControl isInvalid={inputError[ids.vehicleUfPlate]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-uf-plate.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      maxLength="2"
                      variant="main"
                      id={ids.vehicleUfPlate}
                      value={formData[ids.vehicleUfPlate].value.toUpperCase()}
                      onChange={handleInputChange}
                      onFocus={handleVehicleUfPlateFocus}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleUfPlate])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-category*/}
                  <FormControl isInvalid={inputError[ids.vehicleCategory]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-category.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehicleCategory}
                      value={formData[ids.vehicleCategory].value}
                      onChange={handleInputChange}
                      onFocus={handleVehicleCategoryFocus}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleCategory])}
                    </FormErrorMessage>
                  </FormControl>
                  {/*vehicle-model*/}
                  <FormControl isInvalid={inputError[ids.vehicleModel]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-model.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehicleModel}
                      value={formData[ids.vehicleModel].value}
                      onChange={handleInputChange}
                      onFocus={handleVehicleModelFocus}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleModel])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-color*/}
                  <FormControl isInvalid={inputError[ids.vehicleColor]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-color.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehicleColor}
                      value={formData[ids.vehicleColor].value}
                      onChange={handleInputChange}
                      onFocus={handleVehicleColorFocus}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleColor])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-type*/}
                  <FormControl isInvalid={inputError[ids.vehicleType]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-type.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehicleType}
                      value={formData[ids.vehicleType].value}
                      onChange={handleInputChange}
                      onFocus={handleVehicleTypeFocus}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleType])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*vehicle-model-year*/}
                  <FormControl isInvalid={inputError[ids.vehicleModelYear]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.vehicle-model-year.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.vehicleModelYear}
                      value={formData[ids.vehicleModelYear].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.vehicleModelYear])}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
              ) : (
                <Loading h="300px" w="300px" alignSelf="center" justifySelf="center" />
              )}
            </CardBody>
          </Card>
              {/*AIT data card*/}
          <Card>
            <CardHeader mb="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold">
                {t(`${formTranslationKey}.title.ait-data`)}
              </Text>
            </CardHeader>

            <CardBody>
              {formData ? (
                <Stack direction="column" spacing={formRowSpacing}>
                  {/*ait-number*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitNumber]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.ait-number.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.aitNumber}
                      value={formData[ids.aitNumber].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitNumber])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*ait-agent*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitAgent]}>
                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                      {t(`${formTranslationKey}.fields.ait-agent.label`)}
                    </FormLabel>
                    <Input
                      disabled
                      fontSize="sm"
                      variant="main"
                      id={ids.aitAgent}
                      value={formData[ids.aitAgent].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitAgent])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*ait-type*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitType]}>
                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                      {t(`${formTranslationKey}.fields.ait-type.label`)}
                    </FormLabel>
                    <SearchSelect
                      id={ids.aitType}
                      valueField="id"
                      value={formData[ids.aitType].value}
                      isDisabled={!aitTypes?.length}
                      onChange={handleInputChange}
                      placeholder={t(`${formTranslationKey}.fields.ait-type.placeholder`)}
                      displayField="displayLabel"
                      items={aitTypes}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitType])}
                    </FormErrorMessage>
                  </FormControl>

                  <SimpleGrid spacing={formRowSpacing} columns={{ md: 2, lg: 1, "2xl": 2 }}>
                    {/*ait-date*/}
                    <FormControl isRequired isInvalid={inputError[ids.aitDate]}>
                      <FormLabel fontSize="xs" fontWeight="bold">
                        {t(`${formTranslationKey}.fields.ait-date.label`)}
                      </FormLabel>
                      <DateTime
                        dateFormat={DATE_FORMAT}
                        timeFormat={false}
                        locale={LOCALE_PT_BR}
                        value={moment(formData[ids.aitDate].value)}
                        renderInput={(props) => (
                          <Input {...props} fontSize="sm" variant="main" />
                        )}
                        onChange={handleAitDateChange}
                      />
                      <FormErrorMessage>
                        {t(inputError[ids.aitDate])}
                      </FormErrorMessage>
                    </FormControl>

                    {/*ait-time*/}
                    <FormControl isRequired isInvalid={inputError[ids.aitTime]}>
                      <FormLabel fontSize="xs" fontWeight="bold">
                        {t(`${formTranslationKey}.fields.ait-time.label`)}
                      </FormLabel>
                      <DateTime
                        dateFormat={false}
                        timeFormat={TIME_FORMAT}
                        locale={LOCALE_PT_BR}
                        value={moment(formData[ids.aitTime].value, TIME_FORMAT)}
                        renderInput={(props) => (
                          <Input {...props} fontSize="sm" variant="main" />
                        )}
                        onChange={handleAitTimeChange}
                      />
                      <FormErrorMessage>
                        {t(inputError[ids.aitTime])}
                      </FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  {/*ait-observations*/}
                  <FormControl isRequired isInvalid={inputError[ids.aitObservations]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.ait-observations.label`)}
                    </FormLabel>
                    <Textarea
                      ref={observationsTextareaRef}
                      fontSize="sm"
                      variant="main"
                      id={ids.aitObservations}
                      value={formData[ids.aitObservations].value}
                      onChange={handleInputChange}
                      onFocus={handleAitObservationsFocus}
                      rows={2}
                      resize="vertical"
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.aitObservations])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*ait-images*/}
                  <TrafficViolationImagesForm
                    ref={imagesRef}
                    translationKey={formTranslationKey}
                    spacing={cardSpacing}
                    columns={{ md: 2, lg: 1, "2xl": 2 }}
                    disabledFields={[!!imagePath1, !!imagePath2]}
                  />
                </Stack>
              ) : (
                <Loading h="300px" w="300px" alignSelf="center" justifySelf="center" />
              )}
            </CardBody>
          </Card>
          {/*Driver data card*/}
          <Card>
            <CardHeader mb="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold">
                {t(`${formTranslationKey}.title.driver-data`)}
              </Text>
            </CardHeader>

            <CardBody>
              {formData ? (
                <Stack direction="column" spacing={formRowSpacing}>
                  {/*driver-uf-cnh*/}
                  <FormControl isInvalid={inputError[ids.driverUfCnh]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.driver-uf-cnh.label`)}
                    </FormLabel>
                    <Select
                      variant="main"
                      value={formData[ids.driverUfCnh].value}
                      onChange={handleInputChange}
                      fontSize="sm"
                    >
                      <option value='' disabled>Selecione UF</option>
                      {ufs.map((uf) => (
                        <option key={uf.value} value={uf.value}>
                          {uf.value} - {uf.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>
                      {t(inputError[ids.driverUfCnh])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*driver-name*/}
                  <FormControl isInvalid={inputError[ids.driverName]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.driver-name.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.driverName}
                      value={formData[ids.driverName].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.driverName])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*driver-cnh-registration*/}
                  <FormControl isInvalid={inputError[ids.driverCnhRegistration]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.driver-cnh-registration.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.driverCnhRegistration}
                      value={formData[ids.driverCnhRegistration].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.driverCnhRegistration])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*driver-cpf*/}
                  <FormControl isInvalid={inputError[ids.driverCpf]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.driver-cpf.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.driverCpf}
                      maxLength={14}
                      value={formData[ids.driverCpf].value}
                      onChange={(value) => {
                        value.target.value = maskCPF(value.target.value)
                        return handleInputChange(value)
                      }}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.driverCpf])}
                    </FormErrorMessage>
                  </FormControl>
                </Stack>
              ) : (
                <Loading h="300px" w="300px" alignSelf="center" justifySelf="center" />
              )}
            </CardBody>
          </Card>

          {/*Location data card - same column as vehicle/ait/driver when images exist*/}
          {(imagePath1 || imagePath2) && (
            <Card>
            <CardHeader mb="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold">
                {t(`${formTranslationKey}.title.location-data`)}
              </Text>
            </CardHeader>

            <CardBody>
              {formData ? (
                <Stack direction="column" spacing={formRowSpacing}>
                  <FormControl isRequired isInvalid={inputError[ids.locationStreet]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.location-street.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.locationStreet}
                      value={formData[ids.locationStreet].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.locationStreet])}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={inputError[ids.locationNeighborhood]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.location-neighborhood.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.locationNeighborhood}
                      value={formData[ids.locationNeighborhood].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.locationNeighborhood])}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={inputError[ids.locationReference]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.location-reference.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.locationReference}
                      value={formData[ids.locationReference].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.locationReference])}
                    </FormErrorMessage>
                  </FormControl>

                  <SimpleGrid mt={cardSpacing} spacing={formRowSpacing} columns={{ sm: 2, md: 4, lg: 2, "2xl": 4 }}>
                    <Button
                      variant="list-primary"
                      gridRow={1}
                      gridColumn={{ sm: 1, md: 3, lg: 1, "2xl": 3 }}
                      onClick={onToggle}
                    >
                      <Text>
                        {t(`${translationKey}.actions.invalidate`)}
                      </Text>
                    </Button>

                    <Button
                      variant="list-primary"
                      gridRow={1}
                      gridColumn={{ sm: 2, md: 4, lg: 2, "2xl": 4 }}
                      onClick={handleValidate}
                    >
                      <Text>
                        {t(`${translationKey}.actions.validate`)}
                      </Text>
                    </Button>

                    <Button
                      variant="no-effects"
                      bg="gray.100"
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="bold"
                      gridRow={{ sm: 2, md: 1, lg: 2, "2xl": 1 }}
                      gridColumn={1}
                      onClick={redirectToList}
                    >
                      <Text>
                        {t("system.actions.cancel")}
                      </Text>
                    </Button>

                    <Button
                      variant="no-effects"
                      bg="gray.100"
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="bold"
                      gridRow={{ sm: 2, md: 1, lg: 2, "2xl": 1 }}
                      gridColumn={2}
                      onClick={redirectToViolationList}
                    >
                      <Text>
                        {t(`${translationKey}.actions.list-violations`)}
                      </Text>
                    </Button>
                  </SimpleGrid>
                </Stack>
              ) : (
                <Loading h="300px" w="300px" alignSelf="center" justifySelf="center" />
              )}
            </CardBody>
          </Card>
          )}
        </Stack>

        {!(imagePath1 || imagePath2) && (
        <Stack direction="column" spacing={cardSpacing}>
          {/*Location data card - second column only when no images*/}
          <Card>
            <CardHeader mb="20px">
              <Text color={textColor} fontSize="lg" fontWeight="bold">
                {t(`${formTranslationKey}.title.location-data`)}
              </Text>
            </CardHeader>

            <CardBody>
              {formData ? (
                <Stack direction="column" spacing={formRowSpacing}>
                  {/*location-street*/}
                  <FormControl isRequired isInvalid={inputError[ids.locationStreet]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.location-street.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.locationStreet}
                      value={formData[ids.locationStreet].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.locationStreet])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*location-neighborhood*/}
                  <FormControl isInvalid={inputError[ids.locationNeighborhood]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.location-neighborhood.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.locationNeighborhood}
                      value={formData[ids.locationNeighborhood].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.locationNeighborhood])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*location-reference*/}
                  <FormControl isRequired isInvalid={inputError[ids.locationReference]}>
                    <FormLabel fontSize="xs" fontWeight="bold">
                      {t(`${formTranslationKey}.fields.location-reference.label`)}
                    </FormLabel>
                    <Input
                      fontSize="sm"
                      variant="main"
                      id={ids.locationReference}
                      value={formData[ids.locationReference].value}
                      onChange={handleInputChange}
                    />
                    <FormErrorMessage>
                      {t(inputError[ids.locationReference])}
                    </FormErrorMessage>
                  </FormControl>

                  {/*Form actions*/}
                  <SimpleGrid mt={cardSpacing} spacing={formRowSpacing} columns={{ sm: 2, md: 4, lg: 2, "2xl": 4 }}>
                    <Button
                      variant="list-primary"
                      gridRow={1}
                      gridColumn={{ sm: 1, md: 3, lg: 1, "2xl": 3 }}
                      onClick={onToggle}
                    >
                      <Text>
                        {t(`${translationKey}.actions.invalidate`)}
                      </Text>
                    </Button>

                    <Button
                      variant="list-primary"
                      gridRow={1}
                      gridColumn={{ sm: 2, md: 4, lg: 2, "2xl": 4 }}
                      onClick={handleValidate}
                    >
                      <Text>
                        {t(`${translationKey}.actions.validate`)}
                      </Text>
                    </Button>

                    <Button
                      variant="no-effects"
                      bg="gray.100"
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="bold"
                      gridRow={{ sm: 2, md: 1, lg: 2, "2xl": 1 }}
                      gridColumn={1}
                      onClick={redirectToList}
                    >
                      <Text>
                        {t("system.actions.cancel")}
                      </Text>
                    </Button>

                    <Button
                      variant="no-effects"
                      bg="gray.100"
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="bold"
                      gridRow={{ sm: 2, md: 1, lg: 2, "2xl": 1 }}
                      gridColumn={2}
                      onClick={redirectToViolationList}
                    >
                      <Text>
                        {t(`${translationKey}.actions.list-violations`)}
                      </Text>
                    </Button>
                  </SimpleGrid>
                </Stack>
              ) : (
                <Loading h="300px" w="300px" alignSelf="center" justifySelf="center" />
              )}
            </CardBody>
          </Card>
        </Stack>
        )}
      </SimpleGrid>

      {formData && (
        <Modal isOpen={isOpen} onClose={onToggle}>
          <ModalOverlay />
          <ModalContent my="auto">
            <ModalHeader>
              <Flex direction="column">
                <Text color={textColor} fontSize="lg" fontWeight="bold">
                  {t(`${formTranslationKey}.title.invalidate-violation`)}
                </Text>
              </Flex>
            </ModalHeader>

            <ModalCloseButton />

            <ModalBody pb={cardSpacing}>
              <SimpleGrid spacing={cardSpacing} columns={1}>
                <FormControl isRequired isInvalid={inputError[ids.aitInvalidateJustification]}>
                  <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                    {t(`${formTranslationKey}.fields.ait-invalidate-justification.label`)}
                  </FormLabel>
                  <Select
                    fontSize="sm"
                    variant="main"
                    id={ids.aitInvalidateJustification}
                    value={formData[ids.aitInvalidateJustification].value}
                    onChange={handleInputChange}
                    placeholder={t(`${formTranslationKey}.fields.ait-invalidate-justification.placeholder`)}
                  >
                    {invalidateJustifications?.map((option, index) => (
                      <option key={index} value={option.value}>{t(`traffic-violation.labels.status.${option.labelKey}`)}</option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {t(inputError[ids.aitInvalidateJustification])}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={inputError[ids.aitInvalidateObservations]}>
                  <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                    {t(`${formTranslationKey}.fields.ait-invalidate-observations.label`)}
                  </FormLabel>
                  <Textarea
                    fontSize="sm"
                    variant="main"
                    id={ids.aitInvalidateObservations}
                    value={formData[ids.aitInvalidateObservations].value}
                    onChange={handleInputChange}
                    rows={3}
                    resize="vertical"
                    placeholder={t(`${formTranslationKey}.fields.ait-invalidate-observations.placeholder`)}
                  />
                  <FormErrorMessage>
                    {t(inputError[ids.aitInvalidateObservations])}
                  </FormErrorMessage>
                </FormControl>

                <SimpleGrid spacing={cardSpacing} columns={2}>
                  <Button
                    variant="no-effects"
                    bg="gray.100"
                    color="gray.700"
                    fontSize="sm"
                    fontWeight="bold"
                    onClick={onToggle}
                  >
                    <Text>
                      {t("system.actions.cancel")}
                    </Text>
                  </Button>

                  <Button
                    variant="list-primary"
                    isDisabled={!formData[ids.aitInvalidateJustification].value}
                    {...(!formData[ids.aitInvalidateJustification].value ? { _hover: {} } : {})}
                    onClick={handleInvalidate}
                  >
                    <Text>
                      {t(`${translationKey}.actions.invalidate`)}
                    </Text>
                  </Button>
                </SimpleGrid>
              </SimpleGrid>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Flex>
  );
}

export default ViolationAllotmentValidate;
